import React from "react";
import {
  ArrowUpRight,
  FlaskConical,
  Watch,
  Dumbbell,
  Video,
  FolderOpen,
} from "lucide-react";
import { INTAKE_URL } from "@/constants/links";

const DASHBOARD_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/wtrz0ob7_YOZs3eHsBPm0zXfG.png";

const CAPABILITIES = [
  {
    icon: FlaskConical,
    title: "Submit your lab work",
    body: "Upload existing labs or order new panels — they live in your file, not lost in inboxes.",
  },
  {
    icon: Watch,
    title: "Sync your wearable",
    body: "Apple Health, Oura, Whoop, Fitbit, Garmin — sleep, HRV, recovery, activity flow in continuously.",
  },
  {
    icon: Dumbbell,
    title: "Track workouts & body comp",
    body: "Weight, body-fat %, strength, training volume — logged in one place, trended over time.",
  },
  {
    icon: Video,
    title: "Coaching & telehealth visits",
    body: "Video consults with your coach and licensed providers — built into the same app.",
  },
  {
    icon: FolderOpen,
    title: "All your reports, one view",
    body: "Labs, scans, plans, visit notes — everything connected, nothing siloed across portals.",
  },
];

export default function IntegratedProgramSection() {
  return (
    <section
      id="integrated-program"
      className="relative overflow-hidden border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-28"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-40" aria-hidden />
      <div
        className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(30,58,138,0.30), transparent 70%)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        {/* Top: title block */}
        <div className="mb-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="iw-overline mb-3">§ 02 — Inside the program</div>
            <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl xl:text-[3.2rem]">
              One app.{" "}
              <span className="text-[color:var(--iw-blue)]">Every signal you generate.</span>{" "}
              One <span className="text-[color:var(--iw-accent-red)]">personalized plan</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--iw-body)]">
              When you enter the Integrated Optimization Program, you step into one connected system. Labs, wearable data, workouts, weight, body fat, telehealth visits, coaching — all in the same place. Our patent-pending Clinical Intelligence turns it into a medically supervised plan that's actually <em>yours</em>.
            </p>
          </div>
        </div>

        {/* Middle: dashboard visual + capability grid */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start lg:gap-12">
          {/* Visual — 6 cols */}
          <div className="lg:col-span-6">
            <div className="iw-scan-sweep relative overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)]">
              <img
                src={DASHBOARD_IMG}
                alt="Lifelink Clinical Intelligence — biological age, vascular age, epigenetic markers, and longevity insights inside the Integrated Optimization Program"
                className="block aspect-square w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
                style={{ background: "linear-gradient(to top, rgba(17,41,107,0.65) 0%, rgba(17,41,107,0.0) 100%)" }}
                aria-hidden
              />
              {/* Corner brackets */}
              <span className="pointer-events-none absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute right-4 top-4 h-4 w-4 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute left-4 bottom-4 h-4 w-4 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute right-4 bottom-4 h-4 w-4 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />

              <div className="absolute left-4 top-4 inline-flex items-center gap-2 border border-white/40 bg-[color:var(--iw-primary)]/80 px-3 py-1.5 font-mono text-[12px] uppercase tracking-[0.22em] text-white backdrop-blur">
                <span className="iw-pulse-dot block h-1.5 w-1.5 rounded-full bg-[color:var(--iw-accent-red)]" />
                In-app · Live
              </div>

              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                <div className="border-l-2 border-[color:var(--iw-accent-red)] pl-4">
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-blue-bright)]">
                    Your data → Your plan
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold leading-tight tracking-tight text-white sm:text-xl">
                    Connected. <span className="text-[color:var(--iw-accent-red)]">Supervised.</span> Personalized.
                  </div>
                </div>
              </div>
            </div>

            {/* Clinical intelligence callout under the visual */}
            <div className="mt-6 border border-[color:var(--iw-border)] bg-[color:var(--iw-blue-tint-bg)] p-6 sm:p-7">
              <div>
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)] sm:text-[13px]">
                  Patent-pending · Clinical Intelligence
                </div>
                <div className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight text-[color:var(--iw-primary)] sm:text-3xl">
                  The engine that turns your signals into your plan.
                </div>
                <p className="mt-3 text-base leading-relaxed text-[color:var(--iw-body)] sm:text-lg">
                  Your labs, wearable, body composition, and telehealth visits feed our seven patent-pending engines — generating a <strong className="text-[color:var(--iw-primary)]">medically supervised, personalized optimization plan</strong> that updates as your data updates.
                </p>
              </div>
            </div>
          </div>

          {/* Capability list — 6 cols */}
          <div className="lg:col-span-6">
            <div className="iw-overline mb-3">§ Inside the app</div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.05]">
              Everything in one connected system.
            </h3>
            <p className="mt-5 text-lg leading-relaxed text-[color:var(--iw-body)] sm:text-xl">
              You shouldn't need five portals, three apps, and a folder of PDFs. One login. One file. Every layer of your care, talking to each other.
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)]">
              {CAPABILITIES.map((c) => (
                <li
                  key={c.title}
                  className="iw-scan-sweep group flex items-start gap-5 bg-white p-6 transition-colors hover:bg-[color:var(--iw-bg)] sm:p-7"
                >
                  <span className="inline-grid h-14 w-14 flex-shrink-0 place-items-center border border-[color:var(--iw-blue)]/60 text-[color:var(--iw-blue)] transition-colors group-hover:border-[color:var(--iw-blue)] group-hover:bg-[color:var(--iw-blue)]/5">
                    <c.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="font-display text-xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-2xl">
                      {c.title}
                    </div>
                    <p className="mt-2 text-base leading-relaxed text-[color:var(--iw-body)] sm:text-lg">
                      {c.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom: closing claim band — hero image + tagline */}
        <div className="mt-16 grid grid-cols-1 overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] text-white lg:grid-cols-12">
          {/* Hero image — 6 cols */}
          <div className="relative lg:col-span-6">
            <img
              src="https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/sk80iyqm_2b9rpxdv_couple%202.png"
              alt="Optimize Yourself — Integrated Wellness members with biometric overlays, heart-rate, and muscle-mapping HUD"
              className="block aspect-square w-full object-cover lg:aspect-auto lg:h-full"
              loading="lazy"
              decoding="async"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "linear-gradient(to right, rgba(17,41,107,0.0) 60%, rgba(17,41,107,0.45) 100%)" }}
              aria-hidden
            />
          </div>

          {/* Copy — 6 cols */}
          <div className="flex flex-col justify-center gap-5 p-8 sm:p-12 lg:col-span-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--iw-accent-red)] sm:text-[12px]">
              The tagline
            </div>
            <div className="font-display text-5xl font-bold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Optimize{" "}
              <span className="text-[color:var(--iw-accent-red)]">Yourself</span>
              <span className="text-[color:var(--iw-accent-red)]">!</span>
            </div>

            <div className="mt-2 h-px w-16 bg-[color:var(--iw-accent-red)]" aria-hidden />

            <h3 className="font-display text-xl font-semibold leading-tight tracking-tight text-white sm:text-2xl lg:text-3xl">
              What the major companies are trying to build,{" "}
              <span className="text-[color:var(--iw-accent-red)]">we already built</span>.
            </h3>
            <p className="text-base leading-relaxed text-white/95 sm:text-lg">
              One app. Your labs. Your wearable. Your workouts. Your visits. Your plan. Medically supervised. Continuously updated. Already live.
            </p>

            <a
              href={INTAKE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="iw-btn-red mt-3 inline-flex w-fit items-center gap-2 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Get Started <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
